import * as React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, PhoneCall, Play, Plus, Search, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { Page } from '@/components/AppShell';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Checkbox,
  EmptyState,
  Field,
  Input,
  LoadingState,
  Select,
  Stat,
} from '@/components/ui/primitives';
import {
  ConfirmButton,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/overlays';
import { CountyPicker } from '@/components/CountyPicker';
import { STATE_NAMES, SUPPORTED_STATES } from '@shared/counties/index.js';
import { cn, formatPhone, formatRelative, pipelineStatusClass, webPresenceClass } from '@/lib/utils';
import { PIPELINE_STATUSES, WEBSITE_CONDITIONS, WEB_PRESENCE_STATUSES } from '@shared/enums.js';
import type { LeadFilter } from '@shared/schemas.js';
import { OpportunityFilter } from '@/components/OpportunityFilter';

const ORDER_OPTIONS = [
  { value: 'score_desc', label: 'Highest lead score first' },
  { value: 'newest_business', label: 'Newest businesses first' },
  { value: 'rating_desc', label: 'Highest rating first' },
  { value: 'review_count_desc', label: 'Most reviews first' },
  { value: 'oldest_attempt', label: 'Least recently attempted' },
  { value: 'created_desc', label: 'Newest leads first' },
  { value: 'random', label: 'Random' },
] as const;

/**
 * `/queue` is two different screens, because it answers two different questions.
 *
 * A salesperson opens it to answer "what am I doing next?" and gets My work: their
 * own numbers, their own queue, their own follow-ups, their own leads, and one
 * button that starts calling. An owner opens it to answer "what queues exist and
 * how do I build one?" and gets the queue builder, unchanged.
 *
 * The split is presentation. Every figure on the VA screen comes from an endpoint
 * the server already scopes to the caller, so the screen cannot show them anything
 * they are not entitled to even if this component were wrong.
 */
export function CallQueuePage() {
  const { user, authEnabled } = useAuth();
  const isVa = authEnabled && user?.role === 'va';
  return isVa ? <MyWorkScreen /> : <AdminQueueScreen />;
}

function AdminQueueScreen() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [buildOpen, setBuildOpen] = React.useState(params.get('new') === '1');

  const sessions = useQuery({ queryKey: ['call-sessions'], queryFn: () => api.callSessions(false) });
  const presets = useQuery({ queryKey: ['queue-presets'], queryFn: api.queuePresets });

  React.useEffect(() => {
    if (params.get('new') === '1') {
      params.delete('new');
      setParams(params, { replace: true });
    }
    const leadParam = params.get('lead');
    if (leadParam) {
      params.delete('lead');
      setParams(params, { replace: true });
      navigate(`/queue/lead-${leadParam}`);
    }
  }, [params, setParams, navigate]);

  const abandon = useMutation({
    mutationFn: (id: number) => api.abandonSession(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['call-sessions'] });
      toast.success('Queue closed');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const { user, authEnabled } = useAuth();
  const isVa = authEnabled && user?.role === 'va';
  const deletePreset = useMutation({
    mutationFn: (id: number) => api.deleteQueuePreset(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queue-presets'] });
      toast.success('Preset deleted');
    },
  });

  return (
    <Page
      title="Call Queue"
      description="Queues persist across restarts and are re-checked against current lead state every time you open them."
      actions={
        <Button size="sm" onClick={() => setBuildOpen(true)}>
          <Plus className="h-3.5 w-3.5" />
          Build a queue
        </Button>
      }
    >
      <div className="space-y-5 p-5 md:p-6">
        <section>
          <h2 className="section-label mb-2">Active queues</h2>
          {sessions.isLoading ? (
            <LoadingState />
          ) : !sessions.data || sessions.data.length === 0 ? (
            <EmptyState
              icon={PhoneCall}
              title="No active queues"
              description="Build a queue of untouched leads. Previously called leads and follow-ups stay out, along with closed, DNC, archived, merged, cooling-down and phone-less leads."
              action={
                <Button size="sm" onClick={() => setBuildOpen(true)}>
                  <Plus className="h-3.5 w-3.5" />
                  Build a queue
                </Button>
              }
            />
          ) : (
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 xl:grid-cols-3">
              {sessions.data.map((progress) => {
                const done = progress.consumed + progress.skipped;
                const total = progress.session.totalItems;
                const pct = total ? Math.round((done / total) * 100) : 0;
                return (
                  <Card key={progress.session.id}>
                    <CardHeader>
                      <CardTitle className="truncate">{progress.session.name}</CardTitle>
                      <Badge variant={progress.session.status === 'active' ? 'primary' : 'default'}>
                        {progress.session.status}
                      </Badge>
                    </CardHeader>
                    <CardBody className="space-y-2">
                      <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                        <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="grid grid-cols-4 gap-1 text-center text-2xs">
                        <div>
                          <p className="font-semibold tabular-nums">{progress.pending}</p>
                          <p className="text-muted-foreground">left</p>
                        </div>
                        <div>
                          <p className="font-semibold tabular-nums">{progress.consumed}</p>
                          <p className="text-muted-foreground">called</p>
                        </div>
                        <div>
                          <p className="font-semibold tabular-nums">{progress.skipped}</p>
                          <p className="text-muted-foreground">skipped</p>
                        </div>
                        <div>
                          <p className="font-semibold tabular-nums">{progress.removed}</p>
                          <p className="text-muted-foreground">removed</p>
                        </div>
                      </div>

                      {progress.removed > 0 && (
                        <p className="text-2xs text-muted-foreground">
                          Removed:{' '}
                          {Object.entries(progress.removedBreakdown)
                            .filter(([, n]) => n > 0)
                            .map(([reason, n]) => `${n} ${reason.replace('_', ' ')}`)
                            .join(', ')}
                        </p>
                      )}

                      <p className="text-2xs text-muted-foreground">
                        Updated {formatRelative(progress.session.updatedAt)}
                      </p>

                      <div className="flex gap-2">
                        <Button asChild size="sm" className="flex-1" disabled={progress.pending === 0}>
                          <Link to={`/queue/${progress.session.id}`}>
                            <Play className="h-3.5 w-3.5" />
                            {progress.consumed > 0 ? 'Resume' : 'Start'}
                          </Link>
                        </Button>
                        <ConfirmButton
                          title="Close this queue?"
                          description="Progress is kept, but the queue will no longer appear as active."
                          confirmLabel="Close queue"
                          onConfirm={async () => {
                            await abandon.mutateAsync(progress.session.id);
                          }}
                        >
                          <Button variant="outline" size="icon">
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </ConfirmButton>
                      </div>
                    </CardBody>
                  </Card>
                );
              })}
            </div>
          )}
        </section>

        <section>
          <h2 className="section-label mb-2">Presets</h2>
          {!presets.data || presets.data.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No presets saved. Save one while building a queue to reuse the same definition later.
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
              {presets.data.map((preset) => (
                <Card key={preset.id}>
                  <CardBody className="flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{preset.name}</p>
                      <p className="truncate text-2xs text-muted-foreground">
                        {preset.description || `${preset.maxLeads} leads · ${preset.orderBy.replace('_', ' ')}`}
                      </p>
                    </div>
                    <Button
                      size="xs"
                      onClick={async () => {
                        try {
                          const session = await api.createCallSession({ presetId: preset.id });
                          queryClient.invalidateQueries({ queryKey: ['call-sessions'] });
                          navigate(`/queue/${session.id}`);
                        } catch (e) {
                          toast.error((e as Error).message);
                        }
                      }}
                    >
                      Build
                    </Button>
                    {!isVa && (
                      <ConfirmButton
                        title={`Delete “${preset.name}”?`}
                        confirmLabel="Delete"
                        onConfirm={() => deletePreset.mutate(preset.id)}
                      >
                        <button className="text-muted-foreground hover:text-red-300">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </ConfirmButton>
                    )}
                  </CardBody>
                </Card>
              ))}
            </div>
          )}
        </section>
      </div>

      <BuildQueueDialog open={buildOpen} onOpenChange={setBuildOpen} />
    </Page>
  );
}

/**
 * My work — the salesperson's home screen.
 *
 * What is deliberately absent matters as much as what is here: no CRM total, no
 * money, no other person's workload, no assignment controls, no imports, no
 * phone-line administration, no queue builder. Every number is one this VA's own
 * server-scoped endpoints already return them.
 */
function MyWorkScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [starting, setStarting] = React.useState(false);

  // Already scoped to the caller: `dashboardMetrics` reports a VA their own
  // assigned work, and `listSessions` only ever returns queues they created.
  const metrics = useQuery({ queryKey: ['dashboard'], queryFn: api.dashboard });
  const sessions = useQuery({ queryKey: ['call-sessions'], queryFn: () => api.callSessions(false) });
  const followUps = useQuery({ queryKey: ['follow-ups'], queryFn: api.followUps });

  // "Callable right now" — the same eligibility the queue builder applies, asked
  // of the lead list, which restricts a VA to their own assignments server-side.
  const callable = useQuery({
    queryKey: ['my-callable-count'],
    queryFn: () =>
      api.listLeads({ filter: { freshOnly: true, hasPhone: true, cooldownEligibleOnly: true }, page: 1, pageSize: 1 }),
  });

  const [search, setSearch] = React.useState('');
  const [appliedSearch, setAppliedSearch] = React.useState('');
  const [onlyCallable, setOnlyCallable] = React.useState(true);

  const myLeads = useQuery({
    queryKey: ['my-leads', appliedSearch, onlyCallable],
    queryFn: () =>
      api.listLeads({
        filter: {
          freshOnly: true,
          search: appliedSearch || undefined,
          ...(onlyCallable ? { hasPhone: true, cooldownEligibleOnly: true } : {}),
        },
        sortBy: 'normalizedLeadScore',
        sortDir: 'desc',
        page: 1,
        pageSize: 25,
      }),
  });

  // The most recently touched queue is the one "Resume" means.
  const current = sessions.data?.[0] ?? null;
  const remaining = (sessions.data ?? []).reduce((sum, s) => sum + s.pending, 0);
  // Overdue first, then due today — the order someone should work them in. The
  // bucket the item came from is what makes it overdue, so it is carried along
  // rather than recomputed from the date here.
  const due = followUps.data
    ? [
        ...followUps.data.overdue.map((f) => ({ item: f, isOverdue: true })),
        ...followUps.data.dueToday.map((f) => ({ item: f, isOverdue: false })),
      ]
    : [];

  /** One click from "I am here" to "I am calling". */
  const startCalling = async () => {
    setStarting(true);
    try {
      if (current && current.pending > 0) {
        navigate(`/queue/${current.session.id}`);
        return;
      }
      // No live queue: build one from everything assigned to them that is
      // callable today. The filter is resolved server-side and restricted to
      // their own assignments, so this cannot reach anyone else's leads.
      const session = await api.createCallSession({
        name: 'My leads',
        filter: {},
        orderBy: 'score_desc',
        maxLeads: 100,
      });
      await queryClient.invalidateQueries({ queryKey: ['call-sessions'] });
      navigate(`/queue/${session.id}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not start a queue');
    } finally {
      setStarting(false);
    }
  };

  const hasQueue = !!current && current.pending > 0;

  return (
    <Page
      title="My work"
      description={
        metrics.data
          ? `${metrics.data.totalActiveLeads.toLocaleString()} leads assigned to you · ${(
              callable.data?.total ?? 0
            ).toLocaleString()} fresh leads callable now`
          : undefined
      }
      actions={
        <Button size="sm" loading={starting} onClick={startCalling}>
          <Play className="h-3.5 w-3.5" />
          {hasQueue ? 'Resume queue' : 'Start calling'}
        </Button>
      }
    >
      <div className="space-y-4 p-5 md:p-6">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Stat
            label="Assigned to me"
            value={(metrics.data?.totalActiveLeads ?? 0).toLocaleString()}
            hint={`${(callable.data?.total ?? 0).toLocaleString()} fresh leads callable now`}
          />
          <Stat label="Calls today" value={(metrics.data?.callsToday ?? 0).toLocaleString()} />
          <Stat
            label="Follow-ups due"
            value={(metrics.data?.followUpsDue ?? 0).toLocaleString()}
            hint={
              followUps.data?.counts.overdue
                ? `${followUps.data.counts.overdue} overdue`
                : undefined
            }
            valueClassName={followUps.data?.counts.overdue ? 'text-amber-300' : undefined}
          />
          <Stat
            label="Left in queue"
            value={remaining.toLocaleString()}
            hint={current ? `of ${current.session.totalItems}` : 'no queue open'}
          />
        </div>

        {/* ---- Current queue ---- */}
        <Card>
          <CardHeader>
            <CardTitle className="truncate">
              Current queue
              {current && (
                <span className="ml-2 font-normal text-muted-foreground">{current.session.name}</span>
              )}
            </CardTitle>
            {current && (
              <div className="flex shrink-0 gap-2">
                <ConfirmButton
                  title="Close this queue?"
                  description="Progress is kept. The leads stay assigned to you and you can start a new queue whenever you like."
                  confirmLabel="Close queue"
                  onConfirm={() => closeQueue(current.session.id)}
                >
                  <Button variant="outline" size="sm">
                    Close
                  </Button>
                </ConfirmButton>
                <Button size="sm" disabled={current.pending === 0} asChild>
                  <Link to={`/queue/${current.session.id}`}>
                    <PhoneCall className="h-3.5 w-3.5" />
                    Next call
                  </Link>
                </Button>
              </div>
            )}
          </CardHeader>
          <CardBody>
            {sessions.isLoading ? (
              <LoadingState />
            ) : !current ? (
              <p className="text-xs text-muted-foreground">
                No queue open. <span className="text-foreground">Start calling</span> builds one from
                the leads assigned to you that can be called today.
              </p>
            ) : (
              <div className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                  <span className="text-muted-foreground">
                    <span className="font-semibold text-foreground tabular-nums">
                      {current.consumed + current.skipped}
                    </span>{' '}
                    of {current.session.totalItems} worked
                  </span>
                  <span className="text-2xs text-muted-foreground">
                    {current.skipped} skipped · {current.removed} removed (DNC, cooldown, reassigned)
                  </span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full bg-primary transition-all"
                    style={{
                      width: `${
                        current.session.totalItems
                          ? Math.round(
                              ((current.consumed + current.skipped) / current.session.totalItems) * 100,
                            )
                          : 0
                      }%`,
                    }}
                  />
                </div>
                {current.pending === 0 && (
                  <p className="text-2xs text-muted-foreground">
                    Nothing left in this queue. Close it and start a new one when you are ready.
                  </p>
                )}
              </div>
            )}
          </CardBody>
        </Card>

        {/* ---- Follow-ups due ---- */}
        {due.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Your follow-ups</CardTitle>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/follow-ups">See all {due.length} →</Link>
              </Button>
            </CardHeader>
            <CardBody className="divide-y divide-border p-0">
              {due.slice(0, 5).map(({ item, isOverdue }) => (
                <div key={item.id} className="flex items-center gap-3 px-3 py-2">
                  <CalendarClock
                    className={cn('h-3.5 w-3.5 shrink-0', isOverdue ? 'text-red-400' : 'text-amber-400')}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-medium">
                      {item.leadBusinessName ?? `Lead #${item.leadId}`}
                    </p>
                    <p className="truncate text-2xs text-muted-foreground">
                      {item.followUpType}
                      {item.notes ? ` · ${item.notes}` : ''}
                    </p>
                  </div>
                  <span
                    className={cn(
                      'shrink-0 text-2xs',
                      isOverdue ? 'font-medium text-red-300' : 'text-muted-foreground',
                    )}
                  >
                    {item.snoozedUntilDate ?? item.dueDate}
                  </span>
                  <Button variant="outline" size="sm" asChild>
                    <Link to={`/queue/lead-${item.leadId}`}>Call</Link>
                  </Button>
                </div>
              ))}
            </CardBody>
          </Card>
        )}

        {/* ---- My leads ---- */}
        <Card>
          <CardHeader className="flex-wrap gap-2">
            <CardTitle>
              Fresh leads
              <span className="ml-2 font-normal text-muted-foreground">
                Untouched leads assigned to you · worked leads stay in Leads and Follow-Ups
              </span>
            </CardTitle>
            <form
              className="flex shrink-0 items-center gap-1.5"
              onSubmit={(event) => {
                event.preventDefault();
                setAppliedSearch(search.trim());
              }}
            >
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search fresh leads"
                className="h-7 w-44 text-xs"
              />
              <Button type="submit" variant="outline" size="sm">
                <Search className="h-3.5 w-3.5" />
              </Button>
              <Select
                value={onlyCallable ? 'callable' : 'all'}
                onChange={(e) => setOnlyCallable(e.target.value === 'callable')}
                className="h-7 w-32 text-xs"
              >
                <option value="callable">Callable now</option>
                <option value="all">All fresh leads</option>
              </Select>
            </form>
          </CardHeader>
          <CardBody className="p-0">
            {myLeads.isLoading ? (
              <LoadingState />
            ) : (myLeads.data?.rows.length ?? 0) === 0 ? (
              <p className="p-3 text-xs text-muted-foreground">
                {appliedSearch
                  ? 'None of your leads match that search.'
                  : onlyCallable
                    ? 'No untouched leads are callable right now. Check Follow-Ups for scheduled calls or Leads for your call history.'
                    : 'No untouched leads match. Worked leads remain in Leads and Follow-Ups.'}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th className="w-full min-w-[12rem]">Business</th>
                      <th className="w-32">Location</th>
                      <th className="w-36">Phone</th>
                      <th className="w-32">Why call</th>
                      <th className="w-32">Status</th>
                      <th className="w-16 text-right">Calls</th>
                      <th className="w-24 text-right">Last</th>
                      <th className="w-20" />
                    </tr>
                  </thead>
                  <tbody>
                    {myLeads.data!.rows.map((lead) => (
                      <tr key={lead.id}>
                        <td className="font-medium">
                          <Link to={`/leads/${lead.id}`} className="hover:text-primary hover:underline">
                            {lead.businessName}
                          </Link>
                        </td>
                        <td className="whitespace-nowrap text-muted-foreground">
                          {[lead.city, lead.state].filter(Boolean).join(', ') || '—'}
                        </td>
                        <td className="whitespace-nowrap font-mono text-2xs tabular-nums">
                          {formatPhone(lead.primaryPhone)}
                        </td>
                        <td>
                          <Badge className={cn('border', webPresenceClass(lead.webPresenceStatus))}>
                            {lead.webPresenceStatus}
                          </Badge>
                        </td>
                        <td>
                          <Badge className={cn('border', pipelineStatusClass(lead.pipelineStatus))}>
                            {lead.pipelineStatus}
                          </Badge>
                        </td>
                        <td className="text-right tabular-nums text-muted-foreground">
                          {lead.attemptCount}
                        </td>
                        <td className="whitespace-nowrap text-right text-muted-foreground">
                          {lead.lastContactedAt ? formatRelative(lead.lastContactedAt) : 'never'}
                        </td>
                        <td className="text-right">
                          <Button variant="outline" size="sm" asChild>
                            <Link to={`/queue/lead-${lead.id}`}>Call</Link>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </Page>
  );

  async function closeQueue(id: number) {
    try {
      await api.abandonSession(id);
      await queryClient.invalidateQueries({ queryKey: ['call-sessions'] });
      toast.success('Queue closed');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not close the queue');
    }
  }
}

function BuildQueueDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = React.useState('');
  const [filter, setFilter] = React.useState<LeadFilter>({ pipelineStatuses: ['New', 'Ready to Call'] });
  const [orderBy, setOrderBy] = React.useState<string>('score_desc');
  const [maxLeads, setMaxLeads] = React.useState(100);
  const { user, authEnabled } = useAuth();
  const isVa = authEnabled && user?.role === 'va';
  const [savePreset, setSavePreset] = React.useState(false);
  const [presetName, setPresetName] = React.useState('');

  const categories = useQuery({ queryKey: ['categories'], queryFn: api.categories, enabled: open });
  const counties = useQuery({ queryKey: ['counties'], queryFn: api.counties, enabled: open });

  // Live preview of how many leads the current definition would produce.
  const preview = useQuery({
    queryKey: ['queue-preview', filter],
    queryFn: () =>
      api.listLeads({
        filter: { ...filter, freshOnly: true, hasPhone: true, cooldownEligibleOnly: true },
        page: 1,
        pageSize: 1,
      }),
    enabled: open,
  });

  const build = useMutation({
    mutationFn: async () => {
      if (savePreset && presetName.trim()) {
        await api.createQueuePreset({
          name: presetName.trim(),
          filter,
          orderBy: orderBy as never,
          maxLeads,
        });
      }
      return api.createCallSession({ name: name.trim() || null, filter, orderBy: orderBy as never, maxLeads });
    },
    onSuccess: (session) => {
      queryClient.invalidateQueries({ queryKey: ['call-sessions'] });
      queryClient.invalidateQueries({ queryKey: ['queue-presets'] });
      onOpenChange(false);
      navigate(`/queue/${session.id}`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const patch = (p: Partial<LeadFilter>) => setFilter((f) => ({ ...f, ...p }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Build a call queue</DialogTitle>
          <DialogDescription>
            Do Not Contact, archived, merged, cooling-down and phone-less leads are excluded by the query itself.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <Field label="Queue name (optional)">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Clark County roofers" />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <OpportunityFilter filter={filter} onChange={patch} queue />
            <Field label="Pipeline status">
              <Select
                value={filter.pipelineStatuses?.[0] ?? ''}
                onChange={(e) =>
                  patch({ pipelineStatuses: e.target.value ? [e.target.value as never] : undefined })
                }
              >
                <option value="">Any</option>
                {PIPELINE_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Category">
              <Select
                value={filter.categoryIds?.[0] ?? ''}
                onChange={(e) => patch({ categoryIds: e.target.value ? [Number(e.target.value)] : undefined })}
              >
                <option value="">Any</option>
                {categories.data?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="State">
              <Select
                value={filter.stateCodes?.length === 1 ? filter.stateCodes[0] : ''}
                onChange={(e) =>
                  patch({ stateCodes: e.target.value ? [e.target.value as 'OH' | 'MI'] : undefined })
                }
              >
                <option value="">All states</option>
                {SUPPORTED_STATES.map((code) => (
                  <option key={code} value={code}>
                    {STATE_NAMES[code]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="County">
              <CountyPicker
                multiple
                counties={counties.data}
                value={filter.countyIds ?? []}
                onChange={(ids) => patch({ countyIds: ids.length ? ids : undefined })}
                placeholder="Any county"
              />
            </Field>
            {/* Physical is the default, so an existing preset keeps its meaning. */}
            <Field label="County match">
              <Select
                value={filter.countyMode ?? 'physical'}
                onChange={(e) =>
                  patch({
                    countyMode:
                      e.target.value === 'physical' ? undefined : (e.target.value as 'served' | 'either'),
                  })
                }
              >
                <option value="physical">Located in</option>
                <option value="served">Serves</option>
                <option value="either">Located in or serves</option>
              </Select>
            </Field>
            <Field label="City">
              <Input
                value={filter.cities?.[0] ?? ''}
                onChange={(e) => patch({ cities: e.target.value ? [e.target.value] : undefined })}
                placeholder="Any"
              />
            </Field>
            <Field label="Web presence">
              <Select
                value={filter.webPresenceStatuses?.[0] ?? ''}
                onChange={(e) =>
                  patch({ webPresenceStatuses: e.target.value ? [e.target.value as never] : undefined })
                }
              >
                <option value="">Any</option>
                {WEB_PRESENCE_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Website condition">
              <Select
                value={filter.websiteConditions?.[0] ?? ''}
                onChange={(e) =>
                  patch({ websiteConditions: e.target.value ? [e.target.value as never] : undefined })
                }
              >
                <option value="">Any</option>
                {WEBSITE_CONDITIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Min lead score">
              <Input
                type="number"
                min="0"
                max="100"
                value={filter.minScore ?? ''}
                onChange={(e) => patch({ minScore: e.target.value ? Number(e.target.value) : undefined })}
              />
            </Field>
            <Field label="Min rating">
              <Input
                type="number"
                step="0.1"
                min="0"
                max="5"
                value={filter.minRating ?? ''}
                onChange={(e) => patch({ minRating: e.target.value ? Number(e.target.value) : undefined })}
              />
            </Field>
            <Field label="Max prior attempts">
              <Input
                type="number"
                min="0"
                value={filter.maxAttemptCount ?? ''}
                onChange={(e) => patch({ maxAttemptCount: e.target.value ? Number(e.target.value) : undefined })}
              />
            </Field>
            <Field label="Business age">
              <Select
                value={filter.maxBusinessAgeMonths ? String(filter.maxBusinessAgeMonths) : ''}
                onChange={(e) =>
                  patch({ maxBusinessAgeMonths: e.target.value ? Number(e.target.value) : undefined })
                }
              >
                <option value="">Any age</option>
                <option value="12">Under 1 year</option>
                <option value="24">Under 2 years</option>
                <option value="36">Under 3 years</option>
                <option value="48">Under 4 years</option>
              </Select>
            </Field>
            <Field label="Follow-up state">
              <Select
                value={filter.followUpState ?? 'any'}
                onChange={(e) =>
                  patch({ followUpState: e.target.value === 'any' ? undefined : (e.target.value as never) })
                }
              >
                <option value="any">Any</option>
                <option value="none">No open follow-up</option>
                <option value="overdue">Follow-up overdue</option>
                <option value="due_today">Follow-up due today</option>
              </Select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Order">
              <Select value={orderBy} onChange={(e) => setOrderBy(e.target.value)}>
                {ORDER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Max leads">
              <Input
                type="number"
                min="1"
                max="5000"
                value={maxLeads}
                onChange={(e) => setMaxLeads(Number(e.target.value) || 1)}
              />
            </Field>
          </div>

          <div className="rounded-md border border-border bg-surface px-3 py-2 text-xs">
            {preview.isFetching ? (
              'Counting eligible leads…'
            ) : (
              <>
                <span className="font-semibold tabular-nums">{(preview.data?.total ?? 0).toLocaleString()}</span>{' '}
                callable leads match. The queue will take the first{' '}
                <span className="font-semibold tabular-nums">
                  {Math.min(maxLeads, preview.data?.total ?? 0).toLocaleString()}
                </span>
                .
              </>
            )}
          </div>

          {/* Preset creation is an admin-only API; a VA builds ad-hoc queues. */}
          {!isVa && (
            <label className="flex items-center gap-2 text-xs">
              <Checkbox checked={savePreset} onCheckedChange={(v) => setSavePreset(!!v)} />
              Also save this as a reusable preset
            </label>
          )}
          {!isVa && savePreset && (
            <Field label="Preset name">
              <Input value={presetName} onChange={(e) => setPresetName(e.target.value)} />
            </Field>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={build.isPending}
            disabled={(preview.data?.total ?? 0) === 0}
            onClick={() => build.mutate()}
          >
            <PhoneCall className="h-3.5 w-3.5" />
            Build and start
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
